import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { T } from "../../../app/tokens.js";
import { getWeeklyOverview, getWeeklyWeeks, isMondayIso } from "../../../data/projectWeeklyClient.js";
import { weekLabel } from "./weeklyModel.js";
import ProjectWorkingUpdatesView from "./ProjectWorkingUpdatesView.jsx";
import { refreshWeeklyReads, clearWeeklyReads, weeklyReadIdentity, subscribeWeeklyReadIdentity } from "../../../services/projectWeeklyReads.js";

// Discovery is server-owned. Recheck on focus and periodically so an open
// detail crosses UTC Monday without ever choosing an archive or local date.
export function useCurrentProjectWeek(projectId, refreshToken, enabled = true) {
  const identity = useSyncExternalStore(subscribeWeeklyReadIdentity, weeklyReadIdentity);
  const [state, setState] = useState({ projectId, loading: true, refreshing: false, error: null, data: null, weeks: [], revision: 0 });
  const [retry, setRetry] = useState(0);
  const [clock, setClock] = useState(0);
  const frozen = useRef(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => { refreshWeeklyReads(); setClock((n) => n + 1); };
    const timer = window.setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", tick); };
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    const controller = new AbortController();
    // Poll/focus revalidation must not erase a settled identity. A confirmed
    // rollover below invalidates it immediately; failures still surface errors.
    setState((prev) => prev.identity === identity && prev.projectId === projectId && prev.data
      ? { ...prev, refreshing: true, error: null }
      : { projectId, loading: true, refreshing: false, error: null, data: null, weeks: [], revision: prev.revision });
    const invalidateWeek = () => setState((prev) => ({
      ...prev, loading: true, refreshing: false, data: null, weeks: [],
    }));
    (async () => {
      const discovery = await getWeeklyWeeks({ signal: controller.signal });
      if (!active) return;
      if (!isMondayIso(discovery?.currentWeek) || !Array.isArray(discovery?.weeks)) throw new Error("The server did not return a valid current week.");
      const week = discovery.currentWeek;
      setState(prev => ({ ...prev, currentWeek: week }));
      const captured = discovery.weeks.find((entry) => entry.weekDate === week);
      setState((prev) => prev.data && (prev.data.currentWeek !== week
        || (captured?.frozen === true && !prev.data.frozen))
        ? { ...prev, loading: true, refreshing: false, data: null, weeks: [] } : prev);
      const cachedArchive = frozen.current;
      let data = cachedArchive?.identity === identity && cachedArchive.data.weekDate === week && Date.now() < cachedArchive.expires
        ? cachedArchive.data : await getWeeklyOverview(week, { signal: controller.signal });
      if (!active) return;
      // A request can straddle rollover. Follow the response's current week,
      // never display the old week as current while the next request is pending.
      if (data?.currentWeek !== week) {
        if (!isMondayIso(data?.currentWeek)) throw new Error("The server did not return a valid current week.");
        setState(prev => ({ ...prev, currentWeek: data.currentWeek }));
        invalidateWeek();
        data = await getWeeklyOverview(data.currentWeek, { signal: controller.signal });
      }
      if (!data || data.weekDate !== data.currentWeek || !Array.isArray(data.projects)) throw new Error("Current-week updates are unavailable. Please retry.");
      if (!active) return;
      frozen.current = data.frozen ? { identity, data, expires: Date.now() + 30_000 } : null;
      setState((prev) => ({ identity, projectId, loading: false, refreshing: false, error: null, data, weeks: discovery.weeks, revision: prev.revision + 1 }));
    })().catch((error) => {
      if (active) setState((prev) => ({ ...prev, loading: false, refreshing: false, error: error.message || "Could not load current-week updates.", data: null, weeks: [] }));
    });
    return () => { active = false; controller.abort(); };
  }, [projectId, refreshToken, retry, clock, enabled, identity]);
  const visibleState = state.projectId === projectId && (!state.data || state.identity === identity) ? state
    : { loading: true, refreshing: false, error: null, data: null, weeks: [], revision: state.revision };
  return [visibleState, () => { clearWeeklyReads(); frozen.current = null; setRetry((n) => n + 1); }];
}

export function RecentProjectWeeks({ projectId, currentWeek, weeks, cache, onOpenArchive }) {
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: false, entries: [], errors: [] });
  const candidates = weeks.filter((w) => w.frozen && w.weekDate < currentWeek).map((w) => w.weekDate).sort().reverse();
  const key = candidates.join(",");
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setState({ loading: true, entries: [], errors: [] });
    Promise.all(candidates.map(async (week) => {
      try {
        if (!cache.has(week)) {
          const pending = Promise.resolve().then(() => getWeeklyOverview(week)).then((data) => {
            if (!data?.frozen || data.weekDate !== week || !Array.isArray(data.projects)) throw new Error("Frozen archive content is unavailable.");
            return data;
          }).catch((err) => { cache.delete(week); throw err; });
          cache.set(week, pending);
        }
        const data = await cache.get(week);
        return { week, legacy: Boolean(data.legacyUnavailable), member: data.projects.some((p) => p.id === projectId) };
      } catch (error) { return { week, error: error.message || "Could not load archive." }; }
    })).then((results) => {
      if (active) setState({ loading: false, entries: results.filter((r) => !r.error), errors: results.filter((r) => r.error) });
    });
    return () => { active = false; };
  // key captures the ordered catalogue, not the array's render identity.
  }, [open, projectId, currentWeek, key, cache, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  const members = state.entries.filter((e) => e.member && !e.legacy);
  const legacy = state.entries.filter((e) => e.legacy);
  const id = `project-recent-weeks-${projectId}`;
  return <div style={{ marginTop: 12, color: T.mid, fontSize: T.fsXs }}>
    <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)} style={{ border: 0, background: "none", padding: 0, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", textAlign: "left" }}>
      Recent weeks ({open && !state.loading ? members.length : "…"})
    </button>
    {open && <div id={id} style={{ marginTop: 8 }} aria-busy={state.loading}>
      {state.loading ? <p role="status">Loading project archives…</p> : <>
        {members.length > 0 && <ul style={{ margin: 0, paddingLeft: 18 }}>{members.map(({ week }) => <li key={week}><button type="button" onClick={() => onOpenArchive?.(week, projectId)} style={{ border: 0, background: "none", color: T.blue, cursor: "pointer", padding: "4px 0", font: "inherit", textDecoration: "underline" }}>{weekLabel(week)}</button></li>)}</ul>}
        {!candidates.length && <p>No prior frozen weeks are available.</p>}
        {candidates.length > 0 && !members.length && !legacy.length && !state.errors.length && <p>This project is absent from the captured membership of prior frozen weeks.</p>}
        {legacy.map(({ week }) => <p key={week}>{weekLabel(week)}: Weekly Review content was not captured; project membership is unavailable.</p>)}
        {state.errors.map(({ week, error }) => <p role="alert" key={week}>{weekLabel(week)}: {error}</p>)}
        {state.errors.length > 0 && <button type="button" onClick={() => setAttempt((n) => n + 1)} style={{ color: T.blue, background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 8px" }}>Retry archive reads</button>}
      </>}
    </div>}
  </div>;
}

export function ProjectWorkingUpdates({ projectId, canPost, onPost, refreshToken = 0, archiveCache, onOpenArchive }) {
  const [state, retry] = useCurrentProjectWeek(projectId, refreshToken);
  const localCache = useRef(new Map());
  return <ProjectWorkingUpdatesView projectId={projectId} state={state} canPost={canPost} onPost={() => onPost?.(projectId)} onRetry={retry}
    history={state.data && <RecentProjectWeeks key={projectId} projectId={projectId} currentWeek={state.data.currentWeek} weeks={state.weeks} cache={archiveCache || localCache.current} onOpenArchive={onOpenArchive} />} />;
}
